import React, { useRef } from "react";
import { Rnd } from "react-rnd";
import { Scrollbars } from "rc-scrollbars";
import parse from "html-react-parser";
import FroalaEditor from "../../Uitls/CommonFroalaEditor";
import ScrollbarContent from "../../Pages/ScrollbarContent";
import { toNumericStyleValue } from "../../Uitls/Util";
import { useEditorMainPageContext } from "../contexts/EditorMainPageContext";

/** Above every nested layer, which carry ordinary canvas z-indexes. */
const ISI_FRAME_Z_INDEX = 100000;

export const EditorCanvasScrollbarItem = ({ items, index }) => {
  const wasSelectedBeforeGesture = useRef(false);
  const {
    editComponent,
    canvasDimension,
    isAspectRatioLocked,
    handleComponentsDrag,
    handleComponentsDragStop,
    tempLayerScrollbarItems,
    editComponentRef,
    setEditComponent,
    setIsEditorOnScrollbarItems,
    setMultiSelectedComps,
    setToggleSelected,
    handleClickOnCanvas,
    setVisibility,
    clearFroalaTextSelection,
    setSelectedComponent,
    onScrollbarComponentResizeStop,
    onScrollbarComponentsResize,
    handleDeleteKey,
    multiSelectedComps,
    selectedComponent,
    playPause,
    toggleAnimatedBanner,
    onDropScrollbar,
    layerCompData,
    setLayerCompData,
    onDragOverScrollbar,
    setScrollbarItems,
    isEditorOnScrollbarItems,
    isDragged,
    froalaEditorHandleOnclciksAndDrag,
    setFroalaIndex,
    setContainerID,
    createFroalaInstance,
    handleScrollbarComponentsDrag,
    handleScrollbarComponentsDragStop,
    handleCallonResizeStop,
    setCurrentIdZindex,
    sendSelectedComponent,
    layerIndex,
    playPauseRef,
    handleCallonResize,
    handleTextChangeInScrollComp,
    handleCtrlClick,
    scrollBarExpand,
    setDisplayColorPicker,
  } = useEditorMainPageContext();

  if (items?.componentType !== "Scrollbar") return null;

  return (
    <Rnd
      resizeHandleClasses={{
        topRight: `${editComponent?.id === items.id ? "" : "topAnchorRight"}`,
        topLeft: `${editComponent?.id === items.id ? "" : "topAnchorLeft"}`,
        bottomLeft: `${editComponent?.id === items.id ? "" : "bottomAnchorLeft"}`,
        bottomRight: `${editComponent?.id === items.id ? "" : "bottomAnchorRight"}`,
        top: "horizontalAnchorCenter",
        bottom: "horizontalAnchorCenter",
        left: "verticallAnchorCenter",
        right: "verticallAnchorCenter",
      }}
      disableDragging={(() => {
        return editComponent?.id === items.id;
      })()}
      enableResizing={{
        top: !(editComponent?.id === items.id),
        right: !(editComponent?.id === items.id),
        bottom: true,
        left: !(editComponent?.id === items.id),
        topRight: !(editComponent?.id === items.id),
        topLeft: !(editComponent?.id === items.id),
        bottomRight: !(editComponent?.id === items.id),
        bottomLeft: !(editComponent?.id === items.id),
      }}
      size={{
        width: items.componentStyles.width,
        height:
          editComponent?.id === items.id
            ? items.componentStyles.scrollbarHeight
            : items.componentStyles.height,
      }}
      scale={canvasDimension.scale}
      position={{
        x:
          toNumericStyleValue(items.componentStyles.left) +
          (parseInt(items.componentStyles.translateX) || 0),
        y:
          toNumericStyleValue(items.componentStyles.top) +
          (parseInt(items.componentStyles.translateY) || 0),
      }}
      lockAspectRatio={isAspectRatioLocked}
      onDrag={(e, d) => {
        !(editComponent?.id === items.id) && handleComponentsDrag(d, items);
      }}
      onDragStop={(e, d) => {
        !(editComponent?.id === items.id) &&
          handleComponentsDragStop(e, d, items, index);
      }}
      onMouseDown={(e) => {
        // Capture selection state only at the start of the gesture (detail === 1),
        // so a cold double-click selects first and does not enter edit mode.
        if (e.detail === 1) {
          wasSelectedBeforeGesture.current =
            selectedComponent?.id === items.id;
        }
      }}
      onDoubleClick={() => {
        // Edit mode only when scrollbar was already selected before this gesture.
        if (!wasSelectedBeforeGesture.current) {
          setSelectedComponent(items);
          setMultiSelectedComps([items]);
          return;
        }
        const children = items.scrollbarComponents || [];
        tempLayerScrollbarItems.current = children;
        editComponentRef.current = items;
        setEditComponent(items);
        setScrollbarItems(children);
        setIsEditorOnScrollbarItems("dragged");
      }}
      onClick={(e) => {
        // While editing, nested children handle their own selection.
        if (
          editComponent?.id === items.id &&
          e.target?.closest?.(
            ".scrollbar-nested-item, .textCompNew, .buttonResize",
          )
        ) {
          return;
        }

        // Any click on the scrollbar (including over nested content when not
        // editing) selects only the scrollbar container.
        setMultiSelectedComps([items]);
        setSelectedComponent(items);
        setToggleSelected(false);
        handleClickOnCanvas("clickoncanvas");
        setVisibility(false);
        clearFroalaTextSelection(true);

        if (editComponent?.id === items.id) {
          // Clicked empty area inside edit mode — keep edit mode, select container.
          setIsEditorOnScrollbarItems("dragged");
        } else if (editComponent) {
          // Exit edit mode of another scrollbar.
          setEditComponent(undefined);
          editComponentRef.current = undefined;
        }
        setIsEditorOnScrollbarItems("dragged");
      }}
      onResizeStop={(e, direction, ref, delta, position) => {
        onScrollbarComponentResizeStop(items, ref, position);
      }}
      onResize={(e, direction, ref, delta, position) => {
        onScrollbarComponentsResize(items, ref, position);
      }}
      key={index}
      id={items.id}
      tabIndex="0"
      onKeyDown={(e) => {
        if (e.key === "Delete" && !editComponent) {
          setEditComponent(undefined);
          handleDeleteKey(e);
        }
      }}
      className={
        `${
          multiSelectedComps.length < 2 && selectedComponent.id === items.id
            ? `${
                !selectedComponent.componentStyles?.hideBoolean &&
                "selectedComp scrollCompText"
              } div-dragresize-parent cs-container scrollCompText`
            : "div-dragresize-parent cs-container scrollCompText"
        }` +
        " " +
        `${
          multiSelectedComps.length > 1 && items.multiSelected === true
            ? "multipleSelection"
            : ""
        }`
      }
      style={{
        outline: "1px solid black",
        pointerEvents:
          (!playPause.animationCompleted ||
            items.componentStyles.hideBoolean) &&
          "none",
        zIndex: editComponent ? "99999" : items.componentStyles.zIndex,
        opacity:
          toggleAnimatedBanner || items.componentStyles.hideBoolean
            ? items.componentStyles.opacity
            : items.componentStyles.compOpacity / 100,
        webkitTransformOrigin: `${items.componentStyles.transformOriginX}% ${items.componentStyles.transformOriginY}% ${items.componentStyles.transformOriginZ}px`,
        transform: `rotate(${items.componentStyles.rotation}) rotateX(${items.componentStyles.rotationX}) rotateY(${items.componentStyles.rotationY}) scale(${items.componentStyles.scale})`,
      }}
    >
      <ScrollbarContent
        items={items}
        editComponent={editComponent?.id}
        toggleAnimatedBanner={toggleAnimatedBanner}
        isAspectRatioLocked={isAspectRatioLocked}
        onDropScrollbar={onDropScrollbar}
        layerCompData={layerCompData}
        setLayerCompData={setLayerCompData}
        onDragOverScrollbar={onDragOverScrollbar}
        setScrollbarItems={setScrollbarItems}
        scrollbarItems={items.scrollbarComponents}
        playPause={playPause}
        setIsEditorOn={setIsEditorOnScrollbarItems}
        isEditorOn={isEditorOnScrollbarItems}
        canvasDimension={canvasDimension}
        isDragged={isDragged}
        froalaEditorHandleOnclciksAndDrag={froalaEditorHandleOnclciksAndDrag}
        setFroalaIndex={setFroalaIndex}
        setContainerID={setContainerID}
        index={index}
        createFroalaInstance={createFroalaInstance}
        handleScrollbarComponentsDrag={handleScrollbarComponentsDrag}
        handleScrollbarComponentsDragStop={handleScrollbarComponentsDragStop}
        Scrollbars={Scrollbars}
        parse={parse}
        handleCallonResizeStop={handleCallonResizeStop}
        setCurrentIdZindex={setCurrentIdZindex}
        sendSelectedComponent={sendSelectedComponent}
        selectedComponent={selectedComponent}
        layerIndex={layerIndex}
        FroalaEditor={FroalaEditor}
        setIsEditorOnScrollbarItems={setIsEditorOnScrollbarItems}
        isEditorOnScrollbarItems={isEditorOnScrollbarItems}
        playPauseRef={playPauseRef}
        handleCallonResize={handleCallonResize}
        handleTextChangeInScrollComp={handleTextChangeInScrollComp}
        handleCtrlClick={handleCtrlClick}
        scrollBarExpand={scrollBarExpand}
        setDisplayColorPicker={setDisplayColorPicker}
        setVisibility={setVisibility}
        handleDeleteKey={handleDeleteKey}
        setSelectedComponent={setSelectedComponent}
        setMultiSelectedComps={setMultiSelectedComps}
      />
      {/* The export draws this frame as a real border on the scroll container,
          which clips nested content inside it. Repeating that here would shrink
          the authored content box, so the frame is drawn over the nested layers
          instead: their backgrounds must not paint on top of it. Edit mode
          traces its own bounds, so the frame would only double that line. */}
      {editComponent?.id !== items.id && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            boxSizing: "border-box",
            border: items.componentStyles.border,
            borderTopLeftRadius: items.componentStyles.borderTopLeftRadius,
            borderTopRightRadius: items.componentStyles.borderTopRightRadius,
            borderBottomLeftRadius: items.componentStyles.borderBottomLeftRadius,
            borderBottomRightRadius:
              items.componentStyles.borderBottomRightRadius,
            pointerEvents: "none",
            zIndex: ISI_FRAME_Z_INDEX,
          }}
        />
      )}
    </Rnd>
  );
};
